/**
 * Cache invalidation for the Publication Custom CSS view.
 *
 * The caches that carry custom CSS into the story view are:
 *
 * - `['storyView', 'story', { storyId, isTemplate }]` (`useStory`) — the story
 *   document holds both `customCssCode` and `folder.publication.customCssCode`.
 * - `['story', storyId]` (`useFetchStory`, Setup PDF) — same document.
 * - `['storySetupPdf', 'storyHtmlContent', storyId]` — generated HTML plus the
 *   resolved story/publication CSS pair.
 *
 * `refetchType: 'all'` is deliberate. While the user sits on the Custom CSS
 * page those queries have zero observers, and the default `'active'` would only
 * mark them invalidated — the refetch is then supposed to happen when the story
 * view mounts, which was measured NOT to happen here (the story query mounts
 * stale and issues no request, so the old CSS survived the navigation).
 * `'all'` refetches inactive queries immediately, so the story view mounts with
 * correct data and the iframe is built from it on first paint.
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Maps a cached query key back to the story it holds data for, or `undefined`
 * when the key does not carry custom CSS.
 */
export function getStoryIdFromQueryKey(queryKey: readonly unknown[]): string | undefined {
  const [root, second, third] = queryKey;

  if (root === 'storyView' && second === 'story' && typeof third === 'object' && third !== null) {
    const { storyId } = third as { storyId?: unknown };
    return typeof storyId === 'string' ? storyId : undefined;
  }

  if (root === 'storySetupPdf' && second === 'storyHtmlContent' && typeof third === 'string') {
    return third;
  }

  if (root === 'story' && typeof second === 'string') {
    return second;
  }

  return undefined;
}

/**
 * Refetches the CSS-bearing story caches.
 *
 * `storyIds === 'all'` covers a publication-wide CSS change: every cached story
 * inherits the publication's CSS, and enumerating just that publication's
 * stories would mean parsing another cache whose stored shape is not something
 * we can rely on. Only a handful of story entries are ever cached, so
 * refetching all of them is cheaper than getting the scoping subtly wrong.
 */
export function invalidateStoryCssCaches(queryClient: QueryClient, storyIds: string[] | 'all') {
  if (storyIds !== 'all' && storyIds.length === 0) return Promise.resolve();

  const targets = storyIds === 'all' ? null : new Set(storyIds);

  return queryClient.invalidateQueries({
    refetchType: 'all',
    predicate: (query) => {
      const storyId = getStoryIdFromQueryKey(query.queryKey);
      if (storyId === undefined) return false;
      return targets === null || targets.has(storyId);
    }
  });
}
